import React, { useState, useEffect, useRef } from 'react';

import { makeStyles, MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import SkeletonLoader from 'components/SkeletonLoader';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { Divider1, StyledBox, StyledTooltip, StyledTypography } from 'styles/common';

import theme from 'constants/theme';

import { getOrganizationTags } from 'services/admin';

// import { getFilters } from 'services/analytics/engagement';

import { onClickPreCheck } from './CohortCreationFeedback';
import { ExpandedView } from './CohortSidePanel';
import { useCohortCreationContext } from './CohortSidePanelContext';
import { CohortCreationSelect, DropdownContainer, RangeSelector, StyledSelect } from './CommonComponents';

const MODULE_NAME = 'engagement';

const useStyles = makeStyles({
  paper: {
    boxShadow: '3px 4px 5px 0px #0000001C',
    border: `1px solid ${theme.palette.borders.lighterGrey}`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});

/**
 * @deprecated 25-06-2025
 */
const EngagementSource = ({ onClick, accessibleRole, isAllEmployeesSelected, moduleName }) => {
  const [loading, setLoading] = useState(true);
  const [allSources, setAllSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const fetchFilters = () => {
    setLoading(true);

    // getFilters()
    //   .then((res) => {
    //     setAllSources(res.sources);
    //     setSelectedSource(res.sources[0]);
    //   })
    //   .catch((err) => {
    //     console.log(err);
    //   })
    //   .finally(() => {
    //     setLoading(false);
    //   });
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const dispatch = useDispatch();

  const { updateBaseCohort } = useCohortCreationContext();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  useEffect(() => {
    onClick(selectedSource);
  }, [selectedSource]);

  const classes = useStyles();

  const setSelectedSourceCurried = (target) => () => {
    setSelectedSource(target.value);
  };

  if (loading) {
    return <Skeleton width="70%" height="2rem" />;
  }

  return (
    <>
      <StyledBox height="2.5rem" display="flex" alignItems="center" justifyContent="space-between">
        <StyledTypography variant="body2" color="textSecondary" rightGutter={2}>
          Source
        </StyledTypography>
        <StyledSelect
          style={{ height: '100%', width: '10rem' }}
          variant="outlined"
          displayEmpty
          value={selectedSource}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            PopoverClasses: {
              paper: classes.paper,
            },
          }}
          onChange={({ target }) => {
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              setSelectedSourceCurried(target),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            );
          }}
        >
          {allSources.map((source, index) => (
            <MenuItem key={`source-option-${index}`} value={source}>
              <StyledTooltip title={source.displayName}>
                <StyledTypography
                  style={{ whiteSpace: 'no-wrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  variant="body2"
                >
                  {source.displayName}
                </StyledTypography>
              </StyledTooltip>
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledBox>
      <Divider1 topGutter={3} />
    </>
  );
};

const EngagementDrivers = ({ onClick, accessibleRole, isAllEmployeesSelected, moduleName }) => {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const getData = () => {
    getOrganizationTags({ teamId: -1, tagType: 'ENGAGEMENT_DRIVER' })
      .then((orgTags) => {
        const activeTags = orgTags.filter((tag) => tag.active);
        const drivers = activeTags.map((tag) => ({
          key: tag.id,
          displayName: tag.displayName,
          tagSubType: tag.tagSubType,
        }));
        setDrivers(drivers);
        const eNPSDriver =
          drivers.find((driver) => driver.tagSubType === 'ENPS') ??
          drivers.find((driver) => driver.tagSubType === 'MNPS') ??
          '';
        setSelectedDriver(eNPSDriver);
      })
      .finally(() => setLoading(false));
  };

  // get tags
  useEffect(() => {
    setLoading(true);
    getData();
  }, []);

  const dispatch = useDispatch();

  const { updateBaseCohort } = useCohortCreationContext();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  useEffect(() => {
    onClick(selectedDriver);
  }, [selectedDriver]);

  const setSelectedDriverCurried = (target) => () => {
    setSelectedDriver(target);
  };

  if (loading) {
    return <SkeletonLoader skeletons={[100, 100]} />;
  }

  return (
    <StyledBox height="2.5rem" display="flex" alignItems="center" justifyContent="space-between">
      <StyledTypography variant="body2" color="textSecondary" rightGutter={2}>
        Driver
      </StyledTypography>
      <CohortCreationSelect
        options={drivers}
        selectedOption={selectedDriver}
        onChange={(driver) =>
          onClickPreCheck(
            dispatch,
            updateBaseCohort,
            setSelectedDriverCurried(driver),
            accessibleRole,
            isAllEmployeesSelected,
            moduleName,
            enabledFeatures
          )
        }
        defaultOptionText="Select Driver"
      />
    </StyledBox>
  );
};

/**
 * @deprecated 25-06-2025
 */
function CohortCreationEngagement({
  content,
  sectionLabel,
  sectionId,
  onHeaderClick,
  accessibleRole,
  isAllEmployeesSelected,
}) {
  const dispatch = useDispatch();
  const { updateBaseCohort } = useCohortCreationContext();

  const latestValue = useRef(isAllEmployeesSelected);

  useEffect(() => {
    latestValue.current = isAllEmployeesSelected;
  }, [isAllEmployeesSelected]);
  return (
    <>
      <ExpandedView
        content={content}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        headerComponent={() => (
          <EngagementSource
            onClick={(selectedValue) => onHeaderClick(sectionId, 'source', selectedValue)}
            isAllEmployeesSelected={isAllEmployeesSelected}
            accessibleRole={accessibleRole}
            moduleName={MODULE_NAME}
          />
        )}
      />
      <DropdownContainer topGutter={3}>
        <EngagementDrivers
          onClick={(selectedValue) => onHeaderClick(sectionId, 'engagementDrivers', selectedValue)}
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>

      <DropdownContainer topGutter={3}>
        <RangeSelector
          sectionId={sectionId}
          dataKey="data"
          headingText="Score Percentage (Range)"
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>
    </>
  );
}

export default CohortCreationEngagement;
